// src/components/student/psat/StudentPSATTestTaker.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth, db, storage } from '../../../firebaseConfig';
import {
  collection, query, where, getDocs, orderBy, doc, setDoc, getDoc, deleteDoc, serverTimestamp
} from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { LuLayoutGrid, LuBookmark, LuCalculator, LuArrowLeft, LuArrowRight } from "react-icons/lu";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { saveDiagnosticLiveProgress, markDiagnosticLiveProgressComplete } from '../diagnostics/diagnosticLiveProgress';
import { backdropDismiss } from '../../../utils/backdropDismiss';

/**
 * PSAT module names mirror the CSV's Name column exactly.
 * English_Routing_Module → English_Module_A (standard) or English_Module_B (advanced)
 * Math_Routing_Module    → Math_Module_A (standard) or Math_Module_B (advanced)
 */
const INITIAL_STAGE = 'English_Routing_Module';

const TIME_LIMITS = {
  English_Routing_Module: 32 * 60,
  English_Module_A:       32 * 60,
  English_Module_B:       32 * 60,
  Math_Routing_Module:    35 * 60,
  Math_Module_A:          35 * 60,
  Math_Module_B:          35 * 60,
  BREAK:                  10 * 60,
};

const LABELS = {
  English_Routing_Module: 'Reading & Writing - Module 1',
  English_Module_A:       'Reading & Writing - Module 2 (Standard)',
  English_Module_B:       'Reading & Writing - Module 2 (Advanced)',
  Math_Routing_Module:    'Math - Module 1',
  Math_Module_A:          'Math - Module 2 (Standard)',
  Math_Module_B:          'Math - Module 2 (Advanced)',
  BREAK:                  '10 Minute Break',
};

const MATH_MODULES = new Set(['Math_Routing_Module', 'Math_Module_A', 'Math_Module_B']);
const ENGLISH_ROUTING_THRESHOLD = 19; // >= 19 correct → Module_B
const MATH_ROUTING_THRESHOLD = 14;    // >= 14 correct → Module_B

const DESMOS_API_KEY = 'dcb31709b452b1cf9dc26972add0fda6';

function sanitizeExtendedTimePercent(value) {
  const n = Number(value);
  return [0, 25, 50, 75, 100].includes(n) ? n : 0;
}

function getPSATTimeLimit(moduleKey, extendedTimePercent = 0) {
  const base = TIME_LIMITS[moduleKey] ?? 0;
  if (moduleKey === 'BREAK') return base;
  const pct = sanitizeExtendedTimePercent(extendedTimePercent);
  return Math.round(base * (1 + pct / 100));
}

const HEADER_HEIGHT = 64;

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'radial-gradient(circle at 20% -10%, #eff6ff 0, #f8fafc 40%, #ffffff 100%)',
  },
  main: {
    display: 'flex', flexDirection: 'column', gap: 16,
    padding: 16, maxWidth: 900, width: '100%',
    margin: '0 auto', paddingBottom: 96,
  },
  header: {
    height: HEADER_HEIGHT, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 24px',
    background: '#1c3246',
    color: '#fff', zIndex: 30, fontSize: 14, fontWeight: 600,
  },
  logoRow: { display: 'flex', alignItems: 'center' },
  logoImg: { height: 36, width: 'auto', marginRight: 6 },
  logoTitle: { fontSize: 20, fontWeight: 700, letterSpacing: '0.5px', color: '#ffffff' },
  logoDivider: { height: 18, width: 1, background: '#64748b', margin: '0 16px' },
  logoSubtitle: { fontSize: 17, fontWeight: 400, color: '#e2e8f0' },
  headerTools: { display: 'flex', alignItems: 'center', gap: 20, marginLeft: 40 },
  headerBtn: {
    background: 'transparent', border: 'none', color: '#cbd5e1', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700,
    padding: '4px 8px', borderRadius: 4, transition: 'color 0.2s',
  },
  headerBtnActive: { color: '#1c3246', background: '#ffffff', boxShadow: '0 0 10px rgba(255,255,255,0.3)' },
  headerStats: { display: 'flex', alignItems: 'center', gap: 24, fontSize: 14, fontWeight: 700, letterSpacing: '0.5px' },
  statItem: { display: 'flex', alignItems: 'center', gap: 8 },
  instructionPage: { padding: '60px max(5vw, 20px)', maxWidth: '800px', margin: '0 auto', height: '100%', overflowY: 'auto' },
  instructionTitle: { fontSize: 26, fontWeight: 800, color: '#0f172a', marginBottom: 24 },
  instructionText: { fontSize: 18, lineHeight: 1.8, color: '#334155', whiteSpace: 'pre-wrap' },
  startSectionBtn: { marginTop: 40, padding: '14px 32px', fontSize: 16, fontWeight: 700, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', width: '100%' },
  warningBox: { marginTop: 24, padding: 16, borderRadius: 8, background: '#fef2f2', border: '1px solid #f87171', color: '#991b1b', fontWeight: 600, display: 'flex', gap: 12, alignItems: 'center' },
  qWrap: { background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 14, boxShadow: '0 8px 28px rgba(17,24,39,0.06)', overflow: 'hidden' },
  qHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: '1px solid #eef2f7', background: '#fafafa' },
  qBody: { padding: 16 },
  qImgWrap: { display: 'flex', justifyContent: 'center' },
  qImg: { display: 'block', maxWidth: '100%', height: 'auto', margin: '0 auto', border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' },
  fallbackText: { fontSize: 14, color: '#6b7280' },
  optionsCard: { background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 14, boxShadow: '0 8px 28px rgba(17,24,39,0.06)', overflow: 'hidden' },
  optionsHead: { padding: '12px 14px', borderBottom: '1px solid #eef2f7', background: '#fafafa', fontWeight: 700, color: '#111827' },
  optionsContainer: { display: 'flex', flexDirection: 'column', gap: 10, padding: 14 },
  optionRow: (selected, struck) => ({
    display: 'grid', gridTemplateColumns: '36px 1fr auto', alignItems: 'center', gap: 10, padding: 12,
    borderRadius: 12, cursor: 'pointer',
    border: selected ? '2px solid #2563eb' : '1px solid #e5e7eb',
    background: selected ? 'linear-gradient(135deg,#eff6ff,#dbeafe)' : '#ffffff',
    boxShadow: selected ? '0 8px 24px rgba(37,99,235,0.15)' : '0 2px 8px rgba(17,24,39,0.05)',
    opacity: struck ? 0.55 : 1, textDecoration: struck ? 'line-through' : 'none',
  }),
  optionLabel: (selected) => ({
    width: 32, height: 32, display: 'grid', placeItems: 'center', borderRadius: 8,
    background: selected ? '#2563eb' : '#f3f4f6',
    color: selected ? '#ffffff' : '#111827', fontWeight: 800,
  }),
  strikeBtn: (on) => ({
    padding: '6px 10px', borderRadius: 999,
    border: '1px solid ' + (on ? '#f97316' : '#e5e7eb'),
    background: on ? 'linear-gradient(135deg,#fed7aa,#fdba74)' : '#ffffff',
    color: on ? '#7c2d12' : '#111827', fontSize: 12, fontWeight: 700, cursor: 'pointer',
  }),
  floatCalc: {
    position: 'absolute', zIndex: 50, background: '#fff',
    border: '1px solid #cbd5e1', borderRadius: 8,
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)', overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
  },
  floatHeader: {
    padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    cursor: 'move', userSelect: 'none',
  },
  footer: {
    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 40,
    borderTop: '1px solid #e5e7eb', background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)',
  },
  footerInner: { maxWidth: 900, margin: '0 auto', padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  navBtn: (primary) => ({
    padding: '10px 24px', borderRadius: 8,
    border: primary ? 'none' : '1px solid #cbd5e1',
    background: primary ? '#2563eb' : '#ffffff',
    color: primary ? '#ffffff' : '#0f172a',
    fontWeight: 600, cursor: 'pointer', fontSize: 14,
    boxShadow: primary ? '0 4px 12px rgba(37,99,235,0.2)' : 'none',
  }),
  overlay: { position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(17,24,39,0.45)', display: 'grid', placeItems: 'center' },
  gridModal: {
    width: 'min(950px, 96vw)', maxHeight: '80vh', overflow: 'hidden',
    borderRadius: 16, background: '#ffffff', border: '1px solid #e5e7eb',
    boxShadow: '0 40px 90px rgba(17,24,39,0.35)', display: 'flex', flexDirection: 'column',
  },
  gridTitle: { fontWeight: 800, fontSize: 18 },
  gridContent: { padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(56px, 1fr))', gap: 10, overflowY: 'auto' },
  gridItem: (status) => ({
    display: 'grid', placeItems: 'center', height: 56, borderRadius: 12, cursor: 'pointer', fontWeight: 800,
    border: '1px solid #e5e7eb',
    background: status === 'current' ? 'linear-gradient(135deg,#dbeafe,#bfdbfe)' : status === 'answered' ? '#ecfeff' : status === 'bookmarked' ? '#fef3c7' : '#ffffff',
  }),
  breakScreen: { margin: 'auto', display: 'grid', gap: 16, placeItems: 'center' },
  breakTitle: { fontSize: 28, fontWeight: 800 },
  breakTimer: { fontSize: 48, fontWeight: 800, fontVariantNumeric: 'tabular-nums' },
};

// Image loader component
const QuestionImage = ({ path }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!path) { setLoading(false); return; }
    let active = true;
    getDownloadURL(ref(storage, path))
      .then(u => { if (active) { setUrl(u); setLoading(false); } })
      .catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [path]);

  if (loading) return <div style={styles.fallbackText}>Loading Image...</div>;
  if (!url) return <div style={styles.fallbackText}>No image available for this question.</div>;
  return <div style={styles.qImgWrap}><img src={url} alt="Question" style={styles.qImg} /></div>;
};

function normalizeAnswersForQuestions(questions, answersObj) {
  const next = { ...answersObj };
  for (const q of questions || []) {
    if (!(q.questionId in next)) next[q.questionId] = "";
  }
  return next;
}

function normalizedAnswersForModules(allQuestions, modulesTaken, answersObj) {
  const set = new Set(modulesTaken);
  const out = {};
  for (const q of allQuestions) {
    if (set.has(q.module)) out[q.questionId] = (answersObj[q.questionId] ?? "");
  }
  return out;
}

function hashStringToSeed(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h >>> 0;
}

function lcg(seed) {
  let s = seed >>> 0;
  return function () {
    s = (Math.imul(1664525, s) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function shuffleDeterministic(array, seedStr) {
  const arr = array.slice();
  const rnd = lcg(hashStringToSeed(seedStr));
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const FloatingCalculator = ({ geometry, onUpdate, onClose }) => {
  const desmosRef = useRef(null);
  const [dragMode, setDragMode] = useState(null);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startGeo, setStartGeo] = useState(null);

  useEffect(() => {
    const initCalc = () => {
      if (window.Desmos && desmosRef.current && !desmosRef.current.calculator) {
        desmosRef.current.calculator = window.Desmos.GraphingCalculator(desmosRef.current, { keypad: true, expressions: true });
      } else if (!window.Desmos) {
        setTimeout(initCalc, 50);
      }
    };
    initCalc();
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragMode) return;
      e.preventDefault();
      const dx = e.clientX - startPos.x;
      const dy = e.clientY - startPos.y;
      const next = { ...startGeo };
      if (dragMode === 'move') { next.x += dx; next.y += dy; }
      else {
        if (dragMode.includes('e')) next.w = Math.max(300, startGeo.w + dx);
        if (dragMode.includes('s')) next.h = Math.max(200, startGeo.h + dy);
      }
      onUpdate(next);
    };
    const handleMouseUp = () => setDragMode(null);
    if (dragMode) { window.addEventListener('mousemove', handleMouseMove); window.addEventListener('mouseup', handleMouseUp); }
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [dragMode, startPos, startGeo, onUpdate]);

  const startDrag = (e, mode) => { e.stopPropagation(); setDragMode(mode); setStartPos({ x: e.clientX, y: e.clientY }); setStartGeo(geometry); };

  return (
    <div style={{ ...styles.floatCalc, left: geometry.x, top: geometry.y, width: geometry.w, height: geometry.h }}>
      <div style={styles.floatHeader} onMouseDown={(e) => startDrag(e, 'move')}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#333' }}>Desmos Calculator</span>
        <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 16, cursor: 'pointer' }}>✕</button>
      </div>
      <div ref={desmosRef} style={{ flex: 1, width: '100%', height: '100%', position: 'relative' }} />
      <div onMouseDown={(e) => startDrag(e, 'resize-e')} style={{ position: 'absolute', top: 0, right: 0, bottom: 10, width: 8, cursor: 'e-resize', zIndex: 10 }} />
      <div onMouseDown={(e) => startDrag(e, 'resize-s')} style={{ position: 'absolute', bottom: 0, left: 0, right: 10, height: 8, cursor: 's-resize', zIndex: 10 }} />
      <div onMouseDown={(e) => startDrag(e, 'resize-se')} style={{ position: 'absolute', bottom: 0, right: 0, width: 16, height: 16, cursor: 'se-resize', zIndex: 20, background: '#cbd5e1', borderTopLeftRadius: 4 }} />
    </div>
  );
};

function isAnswerCorrect(studentInput, dbAnswerString) {
  if (!studentInput || !dbAnswerString) return false;
  const sStr = studentInput.toString().trim().toUpperCase();
  const options = dbAnswerString.split(',').map(s => s.trim().toUpperCase());
  if (options.includes(sStr)) return true;
  const parseVal = (val) => {
    if (val.includes('/')) { const [n, d] = val.split('/'); return parseFloat(n) / parseFloat(d); }
    return parseFloat(val);
  };
  const sNum = parseVal(sStr);
  if (isNaN(sNum)) return false;
  return options.some((opt) => { const optNum = parseVal(opt); return !isNaN(optNum) && Math.abs(sNum - optNum) < 0.01; });
}

function formatTime(sec) {
  if (sec == null || isNaN(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function StudentPSATTestTaker({ testId, onExit }) {
  const [loadingState, setLoadingState] = useState(true);
  const [currentModule, setCurrentModule] = useState(INITIAL_STAGE);
  const [timeLeft, setTimeLeft] = useState(TIME_LIMITS[INITIAL_STAGE]);
  const [extendedTimePercent, setExtendedTimePercent] = useState(0);
  const [viewingInstructions, setViewingInstructions] = useState(true);
  const [agreeIntegrity, setAgreeIntegrity] = useState(false);
  const [recOn, setRecOn] = useState(true);

  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [fullscreenExitCount, setFullscreenExitCount] = useState(0);
  const [totalInactiveTime, setTotalInactiveTime] = useState(0);
  const blurTimeRef = useRef(null);
  const isInactiveRef = useRef(false);

  const [questions, setQuestions] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [loadingQ, setLoadingQ] = useState(false);

  const [answers, setAnswers] = useState({});
  const [bookmarks, setBookmarks] = useState({});
  const [strikeMap, setStrikeMap] = useState({});

  const [pathHistory, setPathHistory] = useState([]);
  const [showGrid, setShowGrid] = useState(false);

  const [calcOpen, setCalcOpen] = useState(false);
  const [showTimer, setShowTimer] = useState(true);
  const [calcGeo, setCalcGeo] = useState({ x: 100, y: 100, w: 500, h: 400 });
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const userId = auth.currentUser?.uid;

  const formatMinutesLabel = (seconds) => {
    const mins = seconds / 60;
    return Number.isInteger(mins) ? `${mins} minutes` : `${mins.toFixed(1)} minutes`;
  };

  const psatTimeLabel = (moduleKey) => {
    const seconds = getPSATTimeLimit(moduleKey, extendedTimePercent);
    const isExtended = extendedTimePercent > 0 && moduleKey !== 'BREAK';
    return `${formatMinutesLabel(seconds)}${isExtended ? ' (Extended)' : ''}`;
  };

  // Pre-load Desmos script
  useEffect(() => {
    if (document.querySelector('script[data-desmos-api="1.7"]')) return;
    const s = document.createElement('script');
    s.src = `https://www.desmos.com/api/v1.7/calculator.js?apiKey=${encodeURIComponent(DESMOS_API_KEY)}`;
    s.async = true; s.defer = true;
    s.setAttribute('data-desmos-api', '1.7');
    document.body.appendChild(s);
  }, []);

  // Resume / init
  useEffect(() => {
    const initTest = async () => {
      if (!userId) return;
      const accessRef = doc(db, "student_test_access", `${userId}_${testId}`);
      let extPercent = 0;
      try {
        const accessSnap = await getDoc(accessRef);
        if (accessSnap.exists()) extPercent = sanitizeExtendedTimePercent(accessSnap.data().extendedTimePercent);
      } catch (err) { console.warn("Failed to load extended time:", err); }
      setExtendedTimePercent(extPercent);

      const resumeRef = doc(db, "psat_diagnostic_results", userId, "state", "testState");
      try {
        const snap = await getDoc(resumeRef);
        if (snap.exists() && snap.data().testId === testId) {
          const data = snap.data();
          setCurrentModule(data.currentModule || INITIAL_STAGE);
          setViewingInstructions(false);
          setAgreeIntegrity(false);
          setTimeLeft(typeof data.timeLeft === 'number' ? data.timeLeft : getPSATTimeLimit(data.currentModule || INITIAL_STAGE, extPercent));
          if (data.answers) setAnswers(data.answers);
          if (data.bookmarks) setBookmarks(data.bookmarks);
          if (data.strikeMap) setStrikeMap(data.strikeMap);
          if (data.currentQuestionIndex) setQIdx(data.currentQuestionIndex);
          if (data.pathHistory) setPathHistory(data.pathHistory);
          if (data.integrityLog) {
            setTabSwitchCount(data.integrityLog.tabSwitchCount || 0);
            setFullscreenExitCount(data.integrityLog.fullscreenExitCount || 0);
            setTotalInactiveTime(data.integrityLog.totalInactiveTime || 0);
          }
        } else {
          setTimeLeft(getPSATTimeLimit(INITIAL_STAGE, extPercent));
        }
      } catch (err) { console.error("Resume failed:", err); }
      finally { setLoadingState(false); }
    };
    initTest();
  }, [userId, testId]);

  // Load questions for current module
  useEffect(() => {
    if (loadingState) return;
    if (currentModule === 'BREAK' || currentModule === 'FINISH') { setQuestions([]); return; }
    const loadQuestions = async () => {
      setLoadingQ(true);
      try {
        const qy = query(
          collection(db, "psat_diagnostic_tests", testId, "questions"),
          where("module", "==", currentModule),
          orderBy("questionNumber")
        );
        const snap = await getDocs(qy);
        const base = snap.docs.map(d => d.data());
        const seed = `${userId || ''}__${testId || ''}__${currentModule}`;
        const shuffled = shuffleDeterministic(base, seed);
        setQuestions(shuffled);
        setQIdx(prev => (shuffled.length > 0 && prev < shuffled.length ? prev : 0));
      } catch (err) { console.error("Load Error:", err); }
      finally { setLoadingQ(false); }
    };
    loadQuestions();
  }, [currentModule, testId, loadingState, userId]);

  // Close calculator when not in math sections
  useEffect(() => {
    if (!MATH_MODULES.has(currentModule)) setCalcOpen(false);
  }, [currentModule]);

  // Autosave state to psat_diagnostic_results
  const saveTimeout = useRef(null);
  useEffect(() => {
    if (loadingState || viewingInstructions) return;
    const doSave = async () => {
      if (!userId) return;
      const normalized = normalizeAnswersForQuestions(questions, answers);
      const resumeRef = doc(db, "psat_diagnostic_results", userId, "state", "testState");
      try {
        await setDoc(resumeRef, {
          testId, currentModule, currentQuestionIndex: qIdx,
          answers: normalized, bookmarks, strikeMap, timeLeft, pathHistory,
          integrityLog: { tabSwitchCount, fullscreenExitCount, totalInactiveTime },
          updatedAt: serverTimestamp(),
        });
        // Additive: publish a flat, queryable summary for the Admin HomeScreen.
        saveDiagnosticLiveProgress({
          testType: 'PSAT',
          testId,
          studentId: userId,
          studentName: auth.currentUser?.displayName || '',
          stageKey: currentModule,
          answeredCount: questions.reduce((acc, q) => (normalized[q.questionId] ? acc + 1 : acc), 0),
          totalQuestions: questions.length,
          sectionTimeLeftSeconds: timeLeft,
          pathHistory,
        });
        if (normalized !== answers) setAnswers(normalized);
      } catch (err) { console.warn("Autosave failed", err); }
    };
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(doSave, 1000);
    return () => clearTimeout(saveTimeout.current);
  }, [qIdx, answers, bookmarks, strikeMap, timeLeft, currentModule, pathHistory, testId, userId, loadingState, viewingInstructions, questions, tabSwitchCount, fullscreenExitCount, totalInactiveTime]);

  const calculateCurrentScore = useCallback(() => {
    let correct = 0;
    questions.forEach(q => { if (isAnswerCorrect(answers[q.questionId], q.correctAnswer)) correct++; });
    return correct;
  }, [questions, answers]);

  const getNextModule = (current, score) => {
    if (current === 'English_Routing_Module') return score >= ENGLISH_ROUTING_THRESHOLD ? 'English_Module_B' : 'English_Module_A';
    if (current === 'English_Module_A' || current === 'English_Module_B') return 'BREAK';
    if (current === 'BREAK') return 'Math_Routing_Module';
    if (current === 'Math_Routing_Module') return score >= MATH_ROUTING_THRESHOLD ? 'Math_Module_B' : 'Math_Module_A';
    if (current === 'Math_Module_A' || current === 'Math_Module_B') return 'FINISH';
    return 'FINISH';
  };

  // Finish test: grade all questions, save results
  const finishTest = useCallback(async (finalHistory) => {
    if (!userId) return;
    setLoadingState(true);

    const allQSnap = await getDocs(collection(db, "psat_diagnostic_tests", testId, "questions"));
    const allQs = allQSnap.docs.map(d => d.data());

    let englishRaw = 0;
    let mathRaw = 0;
    const modulesTaken = finalHistory.map(h => h.module);
    const takenSet = new Set(modulesTaken);

    allQs.forEach(q => {
      if (!takenSet.has(q.module)) return;
      const studentAns = answers[q.questionId];
      if (isAnswerCorrect(studentAns, q.correctAnswer)) {
        if (q.module.includes('English') || q.module.includes('RW')) englishRaw++;
        if (q.module.includes('Math')) mathRaw++;
      }
    });

    const totalRaw = englishRaw + mathRaw;
    const normalizedAll = normalizedAnswersForModules(allQs, modulesTaken, answers);
    const resultDocId = `${userId}_${testId}`;

    // Derive which Module 2 was taken for each section (used for scaled score lookup)
    const englishModule2 = finalHistory.find(
      h => h.module === 'English_Module_A' || h.module === 'English_Module_B'
    )?.module || null;
    const mathModule2 = finalHistory.find(
      h => h.module === 'Math_Module_A' || h.module === 'Math_Module_B'
    )?.module || null;

    await setDoc(doc(db, "diagnostic_results", resultDocId), {
      studentId: userId,
      type: "PSAT",
      testId,
      status: 'completed',
      completedAt: serverTimestamp(),
      englishRawScore: englishRaw,
      mathRawScore: mathRaw,
      totalRawScore: totalRaw,
      englishModule2,
      mathModule2,
      answers: normalizedAll,
      pathHistory: finalHistory,
      integrityLog: {
        tabSwitchCount, fullscreenExitCount, totalInactiveTime,
        cheated_triggered: (tabSwitchCount > 2 || totalInactiveTime > 30 || fullscreenExitCount > 2),
      },
    });

    await setDoc(
      doc(db, "student_test_access", `${userId}_${testId}`),
      { finished: true, finishedAt: serverTimestamp() },
      { merge: true }
    );

    await deleteDoc(doc(db, "psat_diagnostic_results", userId, "state", "testState"));
    // Mark the live-progress summary completed for the Admin HomeScreen.
    markDiagnosticLiveProgressComplete({
      testType: 'PSAT',
      testId,
      studentId: userId,
      attemptId: resultDocId,
      scoreSummary: { totalRawScore: totalRaw, englishRawScore: englishRaw, mathRawScore: mathRaw },
    });
    alert("PSAT Test Complete! Your responses have been submitted.");
    onExit();
  }, [userId, testId, answers, onExit, tabSwitchCount, fullscreenExitCount, totalInactiveTime]);

  const goToNextSectionWithConfirm = () => setShowConfirmModal(true);

  const handleNextStage = useCallback(async () => {
    const normalized = normalizeAnswersForQuestions(questions, answers);
    if (normalized !== answers) setAnswers(normalized);

    const score = calculateCurrentScore();
    const nextModule = getNextModule(currentModule, score);
    const newHistory = [...pathHistory, { module: currentModule, score }];
    setPathHistory(newHistory);

    const responseId = `${auth.currentUser?.uid}_${testId}_${currentModule}`;
    const moduleAnswers = {};
    for (const q of questions) moduleAnswers[q.questionId] = normalized[q.questionId] ?? "";

    await setDoc(doc(db, "student_responses", responseId), {
      studentId: auth.currentUser?.uid, testId, module: currentModule,
      answers: moduleAnswers, completedAt: serverTimestamp(),
    });

    if (nextModule === 'FINISH') {
      finishTest(newHistory);
    } else {
      setCurrentModule(nextModule);
      setTimeLeft(getPSATTimeLimit(nextModule, extendedTimePercent));
      setQIdx(0);
      window.scrollTo(0, 0);
    }
  }, [questions, answers, calculateCurrentScore, currentModule, pathHistory, testId, finishTest, extendedTimePercent]);

  // Timer
  useEffect(() => {
    if (loadingState || viewingInstructions) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => { if (prev <= 1) { handleNextStage(); return 0; } return prev - 1; });
    }, 1000);
    return () => clearInterval(timer);
  }, [handleNextStage, loadingState, viewingInstructions]);

  // Keyboard nav
  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowRight') { if (qIdx < questions.length - 1) setQIdx(qIdx + 1); }
      if (e.key === 'ArrowLeft') { if (qIdx > 0) setQIdx(qIdx - 1); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [qIdx, questions.length]);

  // Recording blink
  useEffect(() => {
    const id = setInterval(() => setRecOn(prev => !prev), 2000);
    return () => clearInterval(id);
  }, []);

  // Integrity tracking
  useEffect(() => {
    if (loadingState || viewingInstructions || currentModule === 'FINISH') return;
    const handleInactivate = () => {
      if (isInactiveRef.current) return;
      isInactiveRef.current = true;
      blurTimeRef.current = Date.now();
    };
    const handleActivate = () => {
      if (!isInactiveRef.current) return;
      isInactiveRef.current = false;
      if (blurTimeRef.current) {
        const awayTimeSec = Math.floor((Date.now() - blurTimeRef.current) / 1000);
        setTotalInactiveTime(prev => prev + awayTimeSec);
        if (awayTimeSec >= 2) setTabSwitchCount(prev => prev + 1);
        blurTimeRef.current = null;
      }
    };
    const handleVisibilityChange = () => { if (document.hidden) handleInactivate(); else handleActivate(); };
    const handleFullscreenChange = () => { if (!document.fullscreenElement) setFullscreenExitCount(prev => prev + 1); };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleInactivate);
    window.addEventListener("focus", handleActivate);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleInactivate);
      window.removeEventListener("focus", handleActivate);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [loadingState, viewingInstructions, currentModule]);

  const handleAnswer = (val) => {
    if (!questions[qIdx]) return;
    setAnswers(prev => ({ ...prev, [questions[qIdx].questionId]: val }));
  };
  const toggleBookmark = () => {
    if (!questions[qIdx]) return;
    const qId = questions[qIdx].questionId;
    setBookmarks(prev => { const next = { ...prev }; if (next[qId]) delete next[qId]; else next[qId] = true; return next; });
  };
  const toggleStrike = (qId, opt) => {
    setStrikeMap(prev => { const q = { ...(prev[qId] || {}) }; q[opt] = !q[opt]; return { ...prev, [qId]: q }; });
  };

  // ======================== RENDER ========================

  if (loadingState) return <div style={{ padding: 40, textAlign: 'center' }}>Restoring Session...</div>;

  // Instructions screen
  if (viewingInstructions) {
    const handleStartTest = async () => {
      if (questions.length === 0) { alert("Please wait while test questions load."); return; }
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(e => console.log("Fullscreen blocked:", e));
      }
      const responseId = `${auth.currentUser?.uid}_${testId}_${currentModule}`;
      const normalized = normalizeAnswersForQuestions(questions, answers);
      const moduleAnswers = {};
      for (const q of questions) moduleAnswers[q.questionId] = normalized[q.questionId] ?? "";
      try {
        await setDoc(doc(db, "student_responses", responseId), {
          studentId: auth.currentUser?.uid, testId, module: currentModule,
          answers: moduleAnswers, startedAt: serverTimestamp(),
        }, { merge: true });
      } catch (err) { console.error("Failed to initialize student_responses:", err); }
      setViewingInstructions(false);
    };

    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div style={styles.logoRow}>
            <img src="/images/logo.png" alt="Logo" style={styles.logoImg} />
            <span style={styles.logoTitle}>True Education</span>
            <div style={styles.logoDivider}></div>
            <span style={styles.logoSubtitle}>PSAT Assessment</span>
          </div>
        </div>
        <div style={styles.instructionPage}>
          <div style={styles.instructionTitle}><strong>Are you sure you want to take this PSAT test?</strong></div>
          <div style={styles.instructionText}>
            The total test time is <strong>{formatMinutesLabel(
              getPSATTimeLimit('English_Routing_Module', extendedTimePercent) +
              getPSATTimeLimit('English_Module_A', extendedTimePercent) +
              getPSATTimeLimit('BREAK', extendedTimePercent) +
              getPSATTimeLimit('Math_Routing_Module', extendedTimePercent) +
              getPSATTimeLimit('Math_Module_A', extendedTimePercent)
            )}</strong>.<br /><br />
            • Reading &amp; Writing Module 1: <strong>{psatTimeLabel('English_Routing_Module')}</strong><br />
            • Reading &amp; Writing Module 2: <strong>{psatTimeLabel('English_Module_A')}</strong><br />
            • Break: <strong>{psatTimeLabel('BREAK')}</strong><br />
            • Math Module 1: <strong>{psatTimeLabel('Math_Routing_Module')}</strong><br />
            • Math Module 2: <strong>{psatTimeLabel('Math_Module_A')}</strong><br /><br />
            ⚠️ <strong>Once you begin, you cannot pause or exit.</strong>
          </div>
          <div style={styles.warningBox}>
            <div style={{ fontSize: 24 }}>🚨</div>
            <div>
              <strong>OFFICIAL TEST SECURITY NOTICE</strong><br />
              This PSAT diagnostic session is actively monitored in real time.<br /><br />
              <strong>DO NOT</strong> switch tabs, minimize the window, open other applications, or exit full-screen mode.<br />
              These actions are automatically detected and permanently logged to your test record.<br /><br />
              <strong>Repeated violations will trigger immediate submission</strong> and an <strong>Integrity Flag</strong> on your score report.<br />
              Start only if you are ready to test without interruption.
            </div>
          </div>
          <label style={{ display: 'flex', gap: 10, marginTop: 14, fontSize: 14, color: '#334155', alignItems: 'flex-start' }}>
            <input type="checkbox" checked={agreeIntegrity} onChange={(e) => setAgreeIntegrity(e.target.checked)} style={{ marginTop: 4 }} />
            <span>I understand that this session is monitored and that leaving the test page or exiting full-screen may result in automatic submission and a flagged score report.</span>
          </label>
          <div style={{ display: 'flex', gap: 12, marginTop: 40 }}>
            <button type="button" style={{ flex: 1, padding: '14px 32px', fontSize: 16, fontWeight: 700, background: '#ffffff', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: 8, cursor: 'pointer' }} onClick={onExit}>
              Cancel / Go Back
            </button>
            <button
              style={{ ...styles.startSectionBtn, flex: 2, marginTop: 0, opacity: (agreeIntegrity && questions.length > 0) ? 1 : 0.6, cursor: (agreeIntegrity && questions.length > 0) ? 'pointer' : 'not-allowed' }}
              onClick={handleStartTest}
              disabled={!agreeIntegrity || questions.length === 0}
            >
              {questions.length === 0 ? 'Loading Questions...' : 'Start PSAT Test'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const answeredCount = questions.reduce((acc, q) => answers[q.questionId] ? acc + 1 : acc, 0);

  // Break screen
  if (currentModule === 'BREAK') {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div style={styles.logoRow}>
            <img src="/images/logo.png" alt="Logo" style={styles.logoImg} />
            <span style={styles.logoTitle}>True Education</span>
            <div style={styles.logoDivider}></div>
            <span style={styles.logoSubtitle}>10 Minute Break</span>
          </div>
        </div>
        <div style={styles.breakScreen}>
          <div style={styles.breakTitle}>10 Minute Break</div>
          <div style={styles.breakTimer}>{formatTime(timeLeft)}</div>
          <button style={styles.navBtn(true)} onClick={handleNextStage}>Skip Break</button>
        </div>
      </div>
    );
  }

  const currentQ = questions[qIdx];
  const currentVal = currentQ ? (answers[currentQ.questionId] || '') : '';
  const isBookmarked = currentQ ? !!bookmarks[currentQ.questionId] : false;
  const strikes = currentQ ? (strikeMap[currentQ.questionId] || {}) : {};
  const validMC = ['A', 'B', 'C', 'D'];
  const isMCQ = currentQ && validMC.includes((currentQ.correctAnswer || '').trim().toUpperCase());
  const isMath = MATH_MODULES.has(currentModule);

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={styles.logoRow}>
            <img src="/images/logo.png" alt="Logo" style={styles.logoImg} />
            <span style={styles.logoTitle}>True Education</span>
            <div style={styles.logoDivider}></div>
            <span style={styles.logoSubtitle}>{LABELS[currentModule]}</span>
          </div>
          <div style={styles.headerTools}>
            <button style={{ ...styles.headerBtn, ...(showGrid ? styles.headerBtnActive : {}) }} onClick={() => setShowGrid(!showGrid)}>
              <LuLayoutGrid size={16} /> View All
            </button>
            <button style={{ ...styles.headerBtn, ...(isBookmarked ? styles.headerBtnActive : {}) }} onClick={toggleBookmark}>
              <LuBookmark size={16} /> BOOKMARK
            </button>
            {isMath && (
              <button style={{ ...styles.headerBtn, ...(calcOpen ? styles.headerBtnActive : {}) }} onClick={() => setCalcOpen(!calcOpen)}>
                <LuCalculator size={16} /> CALCULATOR
              </button>
            )}
          </div>
        </div>
        <div style={styles.headerStats}>
          <div style={styles.statItem}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#f01523', fontSize: 13, fontWeight: 800, paddingRight: 16, borderRight: '1px solid #2d4a62' }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#f01523', opacity: recOn ? 1 : 0.25, boxShadow: recOn ? '0 0 10px #f01523' : 'none', transition: 'opacity 800ms ease' }} />
              REC
            </div>
            <span style={{ color: '#93c5fd' }}>ANSWERED</span>
            <span>{answeredCount} of {questions.length}</span>
          </div>
          <div style={styles.statItem}>
            <span style={{ color: '#93c5fd' }}>TOTAL TIME LEFT</span>
            <span style={{ fontVariantNumeric: 'tabular-nums', visibility: showTimer ? 'visible' : 'hidden' }}>{formatTime(timeLeft)}</span>
            <button onClick={() => setShowTimer(!showTimer)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 4, display: 'flex' }}>
              {showTimer ? <FaEye size={18} /> : <FaEyeSlash size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div style={styles.main}>
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={styles.qWrap}>
            <div style={styles.qHead}>
              <div style={{ fontWeight: 700 }}>Question {qIdx + 1}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Original # {currentQ?.questionNumber ?? '-'}</div>
            </div>
            <div style={styles.qBody}>
              {loadingQ ? (
                <div style={{ margin: '12px 0', color: '#6b7280' }}>Loading...</div>
              ) : (
                currentQ ? <QuestionImage path={currentQ.imagePath} /> : <div style={styles.fallbackText}>No question.</div>
              )}
            </div>
          </div>

          <div style={styles.optionsCard}>
            <div style={styles.optionsHead}>Your Answer</div>
            <div style={styles.optionsContainer}>
              {isMCQ ? (
                ['A', 'B', 'C', 'D'].map(opt => {
                  const selected = currentVal === opt;
                  const struck = !!(strikes[opt]);
                  return (
                    <div key={opt} style={styles.optionRow(selected, struck)} onClick={() => handleAnswer(opt)} title={struck ? 'Struck out' : 'Select'}>
                      <div style={styles.optionLabel(selected)}>{opt}</div>
                      <div style={{ fontWeight: 700, color: struck ? '#6b7280' : '#111827' }}>Option {opt}</div>
                      <button type="button" style={styles.strikeBtn(struck)} onClick={(e) => { e.stopPropagation(); toggleStrike(currentQ.questionId, opt); }}>
                        {struck ? 'Un-strike' : 'Strike'}
                      </button>
                    </div>
                  );
                })
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  <label style={{ fontSize: 13, color: '#4b5563' }}>Enter your answer:</label>
                  <input
                    type="text"
                    value={currentVal}
                    onChange={(e) => handleAnswer(e.target.value)}
                    placeholder="Type your answer"
                    style={{ width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid #e5e7eb', background: '#f9fafb', fontSize: 14 }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div style={styles.footer}>
        <div style={styles.footerInner}>
          <div style={{ color: '#64748b', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
            Use
            <kbd style={{ fontFamily: 'inherit', border: '1px solid #cbd5e1', padding: '2px 6px', borderRadius: 4, display: 'inline-flex', alignItems: 'center', background: '#f8fafc', color: '#334155' }}><LuArrowLeft size={14} /></kbd>
            <kbd style={{ fontFamily: 'inherit', border: '1px solid #cbd5e1', padding: '2px 6px', borderRadius: 4, display: 'inline-flex', alignItems: 'center', background: '#f8fafc', color: '#334155' }}><LuArrowRight size={14} /></kbd>
            for quick nav
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button style={styles.navBtn(false)} disabled={qIdx === 0} onClick={() => setQIdx(idx => Math.max(idx - 1, 0))}>Previous</button>
            <button style={styles.navBtn(true)} onClick={() => { if (qIdx < questions.length - 1) setQIdx(qIdx + 1); else goToNextSectionWithConfirm(); }}>
              {qIdx === questions.length - 1 ? 'Submit' : 'Next'}
            </button>
          </div>
        </div>
      </div>

      {/* CALCULATOR */}
      {calcOpen && isMath && (
        <FloatingCalculator geometry={calcGeo} onUpdate={setCalcGeo} onClose={() => setCalcOpen(false)} />
      )}

      {/* CONFIRM MODAL */}
      {showConfirmModal && (
        <div style={styles.overlay}>
          <div style={{ ...styles.gridModal, width: 400, padding: '32px 24px', textAlign: 'center', margin: 'auto' }}>
            <h3 style={{ marginTop: 0, fontSize: 20, color: '#0f172a' }}>Submit Section?</h3>
            <p style={{ color: '#475569', marginBottom: 24, lineHeight: 1.5 }}>
              Are you sure you want to submit this section and move on? You cannot return to these questions.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button style={{ ...styles.navBtn(false), flex: 1 }} onClick={() => setShowConfirmModal(false)}>Cancel</button>
              <button style={{ ...styles.navBtn(true), flex: 1 }} onClick={() => { setShowConfirmModal(false); handleNextStage(); }}>Yes, Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* REVIEW GRID */}
      {showGrid && (
        <div style={styles.overlay} {...backdropDismiss(() => setShowGrid(false))}>
          <div style={styles.gridModal} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottom: '1px solid #eef2f7' }}>
              <div style={styles.gridTitle}>Question Review</div>
              <button onClick={() => setShowGrid(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>✕</button>
            </div>
            <div style={styles.gridContent}>
              {questions.map((q, idx) => {
                const isAns = !!answers[q.questionId];
                const isMark = !!bookmarks[q.questionId];
                const isCurr = idx === qIdx;
                let status = 'unanswered';
                if (isAns) status = 'answered';
                if (isMark) status = 'bookmarked';
                if (isCurr) status = 'current';
                return (
                  <div key={q.questionId} style={styles.gridItem(status)} onClick={() => { setQIdx(idx); setShowGrid(false); }}>
                    {idx + 1}
                  </div>
                );
              })}
            </div>
            <div style={{ padding: '10px 16px', borderTop: '1px solid #eef2f7', display: 'flex', gap: 16, fontSize: 12, color: '#6b7280' }}>
              <span>⬜ Unanswered</span><span style={{ color: '#0e7490' }}>⬜ Answered</span><span style={{ color: '#d97706' }}>⬜ Bookmarked</span><span style={{ color: '#2563eb' }}>⬜ Current</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
